import React, { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Alert,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Stack,
} from '@mui/material';
import {
  UploadFile as UploadFileIcon,
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  RemoveCircleOutline as SkipIcon,
} from '@mui/icons-material';
import { useNotification } from '@ipc/ipc-components';
import { Sidebar } from '../../../common/components/Sidebar';
import SectionBar from '../../../common/components/SectionBar/SectionBar';
import { useNavItems } from '../../../hooks';
import * as Styled from '../Help.styled';
import { unzip, basename } from './zip';
import { processGuide, GuideSection } from './guideProcessor';
import { uploadGuide, UploadProgress, UploadOutcome } from './guideUploader';

const IMAGE_EXTS = ['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp', 'bmp'];

const STEPS = ['Upload guide (.zip)', 'Preview sections', 'Upload help content'];

function isImageName(name: string): boolean {
  const ext = name.toLowerCase().split('.').pop() || '';
  return IMAGE_EXTS.includes(ext);
}

const HelpImport: React.FC = () => {
  const navigate = useNavigate();
  const navItems = useNavItems();
  const { successNotification, errorNotification } = useNotification();

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeStep, setActiveStep] = useState(0);

  const [fileName, setFileName] = useState('');
  const [guideText, setGuideText] = useState('');
  const [guideFilename, setGuideFilename] = useState('ipep-user-guide.md');
  const [images, setImages] = useState<Map<string, Uint8Array>>(new Map());
  const [sections, setSections] = useState<GuideSection[]>([]);
  const [problems, setProblems] = useState<string[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [parsing, setParsing] = useState(false);

  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const [outcome, setOutcome] = useState<UploadOutcome | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const stats = useMemo(() => {
    const enabled = sections.filter((s) => s.enabled);
    const disabled = sections.length - enabled.length;
    const unmapped = sections.filter((s) => !s.mapped).length;
    const missing = new Set<string>();
    enabled.forEach((s) =>
      s.imageRefs.forEach((r) => {
        if (!images.has(basename(r).toLowerCase())) missing.add(r);
      })
    );
    return { total: sections.length, enabled: enabled.length, disabled, unmapped, missingImages: missing.size };
  }, [sections, images]);

  const reset = () => {
    setFileName('');
    setGuideText('');
    setImages(new Map());
    setSections([]);
    setProblems([]);
    setParseError(null);
    setOutcome(null);
    setProgress(null);
    setActiveStep(0);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFile = async (file: File) => {
    setParsing(true);
    setParseError(null);
    try {
      const lower = file.name.toLowerCase();
      let text = '';
      let mdName = 'ipep-user-guide.md';
      const imageMap = new Map<string, Uint8Array>();

      if (lower.endsWith('.zip')) {
        const buf = await file.arrayBuffer();
        const { files } = await unzip(buf);

        // Pick the guide markdown: prefer ipep-user-guide.md, else the largest .md.
        let best: { name: string; bytes: Uint8Array } | null = null;
        for (const [name, bytes] of files) {
          if (!name.toLowerCase().endsWith('.md')) continue;
          if (basename(name).toLowerCase() === 'ipep-user-guide.md') {
            best = { name, bytes };
            break;
          }
          if (!best || bytes.length > best.bytes.length) best = { name, bytes };
        }
        if (!best) throw new Error('No .md file found inside the .zip.');
        text = new TextDecoder('utf-8').decode(best.bytes);
        mdName = basename(best.name);

        for (const [name, bytes] of files) {
          if (isImageName(name)) imageMap.set(basename(name).toLowerCase(), bytes);
        }
      } else if (lower.endsWith('.md')) {
        text = await file.text();
        mdName = file.name;
      } else {
        throw new Error('Please choose a .zip (markdown + images) or a .md file.');
      }

      const { sections: secs, problems: probs } = processGuide(text);
      if (!secs.length) throw new Error('No help sections were found in the guide.');

      setFileName(file.name);
      setGuideText(text);
      setGuideFilename(mdName);
      setImages(imageMap);
      setSections(secs);
      setProblems(probs);
      setActiveStep(1);
    } catch (err) {
      setParseError(err instanceof Error ? err.message : 'Failed to read the file.');
    } finally {
      setParsing(false);
    }
  };

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  const handleUpload = async () => {
    setUploading(true);
    setOutcome(null);
    setProgress(null);
    try {
      const result = await uploadGuide({
        sections,
        images,
        fullGuideText: guideText,
        guideFilename,
        onProgress: setProgress,
      });
      setOutcome(result);
      const failed = result.results.filter((r) => r.status === 'failed').length;
      const uploaded = result.results.filter((r) => r.status === 'uploaded').length;
      if (failed) errorNotification(`Uploaded ${uploaded} section(s), ${failed} failed. See the results below.`);
      else successNotification(`Uploaded ${uploaded} help section(s) and ${result.imagesUploaded} image(s).`);
    } catch (err) {
      errorNotification(err instanceof Error ? err.message : 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const renderUploadStep = () => (
    <Box
      data-testid='help-import-dropzone'
      sx={{
        border: '2px dashed', borderColor: 'divider', borderRadius: 2, p: 5, textAlign: 'center',
        cursor: 'pointer', '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
      }}
      onClick={() => fileInputRef.current?.click()}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        const f = e.dataTransfer.files?.[0];
        if (f) handleFile(f);
      }}
    >
      <UploadFileIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
      <Typography variant='h6'>Drop the guide .zip here, or click to choose</Typography>
      <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
        The .zip should contain the guide markdown (e.g. <code>ipep-user-guide.md</code>) and an <code>images/</code> folder.
        A plain <code>.md</code> file is also accepted (without images).
      </Typography>
      <input
        ref={fileInputRef}
        type='file'
        accept='.zip,.md'
        hidden
        onChange={onInputChange}
        data-testid='help-import-file-input'
      />
      {parsing && <LinearProgress sx={{ mt: 3 }} />}
      {parseError && <Alert severity='error' sx={{ mt: 3, textAlign: 'left' }}>{parseError}</Alert>}
    </Box>
  );

  const renderPreviewStep = () => (
    <Box>
      <Stack direction='row' spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Chip label={`File: ${fileName}`} variant='outlined' />
        <Chip color='primary' label={`${stats.total} sections`} />
        <Chip color='success' label={`${stats.enabled} will upload`} />
        <Chip label={`${stats.disabled} conceptual (skipped)`} />
        <Chip label={`${images.size} images bundled`} />
        {stats.missingImages > 0 && <Chip color='warning' label={`${stats.missingImages} image refs missing`} />}
        {stats.unmapped > 0 && <Chip color='error' label={`${stats.unmapped} unmapped headings`} />}
      </Stack>

      {problems.length > 0 && (
        <Alert severity='warning' sx={{ mb: 2 }}>
          <Typography variant='subtitle2' sx={{ fontWeight: 600 }}>Heading / map mismatches ({problems.length})</Typography>
          <Box component='ul' sx={{ m: 0, pl: 3 }}>
            {problems.slice(0, 12).map((p, i) => (<li key={i}><Typography variant='body2'>{p}</Typography></li>))}
            {problems.length > 12 && <li><Typography variant='body2'>…and {problems.length - 12} more</Typography></li>}
          </Box>
        </Alert>
      )}

      <TableContainer component={Paper} variant='outlined' sx={{ maxHeight: 460 }} data-testid='help-import-preview-table'>
        <Table size='small' stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Lvl</TableCell>
              <TableCell>Section</TableCell>
              <TableCell>Page URL</TableCell>
              <TableCell>data-testid</TableCell>
              <TableCell align='right'>Images</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sections.map((s) => {
              const missing = s.imageRefs.filter((r) => !images.has(basename(r).toLowerCase())).length;
              return (
                <TableRow key={s.id} hover>
                  <TableCell>{s.num}</TableCell>
                  <TableCell>H{s.level}</TableCell>
                  <TableCell sx={{ pl: (s.level - 1) * 1.5 }}>{s.title}</TableCell>
                  <TableCell><code>{s.pageurl}</code></TableCell>
                  <TableCell><code>{s.datatestid || '—'}</code></TableCell>
                  <TableCell align='right'>
                    {s.imageRefs.length}{missing > 0 && <Typography component='span' color='warning.main'> ({missing}!)</Typography>}
                  </TableCell>
                  <TableCell>
                    {!s.mapped ? <Chip size='small' color='error' label='unmapped' />
                      : s.enabled ? <Chip size='small' color='success' label='upload' />
                        : <Chip size='small' label='skip' />}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack direction='row' spacing={2} sx={{ mt: 3 }}>
        <Button variant='outlined' onClick={reset}>Choose another file</Button>
        <Button
          variant='contained'
          startIcon={<CloudUploadIcon />}
          onClick={() => setActiveStep(2)}
          disabled={stats.enabled === 0}
          data-testid='help-import-continue-button'
        >
          Continue to upload ({stats.enabled})
        </Button>
      </Stack>
    </Box>
  );

  const renderUploadResults = () => (
    <Box>
      <Alert severity='info' sx={{ mb: 2 }}>
        This will upload {stats.enabled} help section(s) and their images to the server, replacing existing entries with the same id.
      </Alert>

      {!outcome && (
        <Button
          variant='contained'
          size='large'
          startIcon={<CloudUploadIcon />}
          onClick={handleUpload}
          disabled={uploading}
          data-testid='help-import-upload-button'
        >
          {uploading ? 'Uploading…' : `Upload everything (${stats.enabled} sections, ${images.size} images)`}
        </Button>
      )}

      {uploading && progress && (
        <Box sx={{ mt: 3 }}>
          <Typography variant='body2' sx={{ mb: 1 }}>
            {progress.phase === 'images' ? 'Uploading images' : progress.phase === 'sections' ? 'Uploading sections' : 'Finishing'}
            {' — '}{progress.message}
          </Typography>
          <LinearProgress variant='determinate' value={progress.total ? (progress.current / progress.total) * 100 : 0} />
        </Box>
      )}

      {outcome && (
        <Box sx={{ mt: 3 }}>
          <Stack direction='row' spacing={1} sx={{ mb: 2 }}>
            <Chip color='success' label={`${outcome.results.filter((r) => r.status === 'uploaded').length} uploaded`} />
            <Chip label={`${outcome.results.filter((r) => r.status === 'skipped').length} skipped`} />
            <Chip color='error' label={`${outcome.results.filter((r) => r.status === 'failed').length} failed`} />
            <Chip color='primary' label={`${outcome.imagesUploaded} images`} />
          </Stack>

          {outcome.missingImages.length > 0 && (
            <Alert severity='warning' sx={{ mb: 2 }}>
              {outcome.missingImages.length} image(s) referenced in the guide were not found in the bundle: {outcome.missingImages.slice(0, 8).map(basename).join(', ')}{outcome.missingImages.length > 8 ? '…' : ''}
            </Alert>
          )}

          <TableContainer component={Paper} variant='outlined' sx={{ maxHeight: 420 }}>
            <Table size='small' stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Section</TableCell>
                  <TableCell>Page URL</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Detail</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {outcome.results.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell>{r.title}</TableCell>
                    <TableCell><code>{r.pageurl}</code></TableCell>
                    <TableCell>
                      {r.status === 'uploaded' && <Chip size='small' color='success' icon={<CheckCircleIcon />} label='uploaded' />}
                      {r.status === 'skipped' && <Chip size='small' icon={<SkipIcon />} label='skipped' />}
                      {r.status === 'failed' && <Chip size='small' color='error' icon={<CancelIcon />} label='failed' />}
                    </TableCell>
                    <TableCell><Typography variant='body2' color='text.secondary'>{r.detail}</Typography></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Stack direction='row' spacing={2} sx={{ mt: 3 }}>
            <Button variant='outlined' onClick={reset}>Import another guide</Button>
            <Button variant='contained' onClick={() => navigate('/help')}>Back to Help Center</Button>
          </Stack>
        </Box>
      )}

      {!outcome && !uploading && (
        <Button variant='text' sx={{ mt: 2, ml: 2 }} onClick={() => setActiveStep(1)}>Back to preview</Button>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }} data-testid='help-import-container'>
      <Grid container>
        <Grid item xs={12}>
          <SectionBar
            title='Help Center'
            subtitle='Import User Guide'
            onToggleSidebar={() => setSidebarOpen((p) => !p)}
            isSidebarOpen={sidebarOpen}
            onSubtitleClick={() => navigate('/help')}
            data-testid='help-import-section-bar'
          />
        </Grid>

        <Grid item xs={12} sx={{ display: 'flex', position: 'relative' }}>
          {sidebarOpen && (
            <Box sx={{ position: 'relative' }}>
              <Sidebar isOpen={sidebarOpen} onToggle={() => setSidebarOpen((p) => !p)} navItems={navItems} data-testid='help-import-sidebar' />
            </Box>
          )}

          <Styled.MainContent sidebarOpen={sidebarOpen} data-testid='help-import-main-content'>
            <Styled.Container>
              <Typography variant='h5' sx={{ mb: 1, fontWeight: 600 }}>Import User Guide</Typography>
              <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
                Upload the full guide once. It is annotated from the built-in section map, split per heading, and each
                section is published to its page&apos;s help icon — images included.
              </Typography>

              <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                {STEPS.map((label) => (<Step key={label}><StepLabel>{label}</StepLabel></Step>))}
              </Stepper>

              {activeStep === 0 && renderUploadStep()}
              {activeStep === 1 && renderPreviewStep()}
              {activeStep === 2 && renderUploadResults()}
            </Styled.Container>
          </Styled.MainContent>
        </Grid>
      </Grid>
    </Box>
  );
};

export default HelpImport;
